import { useState, useEffect, useCallback } from "react";
import { PreapprovalResponse } from "../register/types";
import { useLocation } from "react-router-dom";
import { LargeContentBox } from "@/components/common";
import { toast } from "react-toastify";
import { Grid, Stack, Box } from "@mui/material";
import { ExtensionCard } from "./components/ExtensionCard";
import { MarketplaceCard } from "./components/MarketplaceCard";
import { GoogleAuthCard } from "./components/GoogleAuthCard";
import { SectionTitle } from "./components/SectionTitle";
import { MainPageTitle } from "@/components/common/MainPageTitle";
import { useFluxPayContractContext } from "@/contexts/FluxPayContractContext";
import { useMedicalReportsAgentContext } from "@/contexts/MedicalReportsAgentContext";
import { useGuidanceAgentContext } from "@/contexts/GuidanceAgentContext";
import { MedicalReportsAgentCard } from "./components/MedicalReportsAgentCard";
import { GuidanceAgentCard } from "./components/GuidanceAgentCard";
import { DiscussionCaseCard } from "./components/DiscussionCaseCard";
import { useDiscussionCaseContext } from "@/contexts/DiscussionCaseContext";
import { PostAttendanceCard } from "./components/PostAttendanceCard";

import { usePostAttendanceContext } from "@/contexts/PostAttendanceContext";
import { useHealthPlanContractContext } from "@/contexts/HealthPlanContractContext";
import { useCRMContractContext } from "@/contexts/CRMContractContext";
import { useFluxSignerContext } from "@/contexts/FluxSignerContext";
import { useQuestionnairesContractContext } from "@/contexts/QuestionnairesContractContext";
import { HealthPlanCard } from "./components/HealthPlanCard";
import { ProntoAtendimentoExtensionCard } from "./components/ProntoAtendimentoExtensionCard";
import { CRMCard } from "./components/CRMCard";
import { FluxSignerCard } from "./components/FluxSignerCard";
import { QuestionnairesCard } from "./components/QuestionnairesCard";
import { useContractContext } from "@/contexts/ContractContext";
import { PaymentPostAttendance } from "./components/PaymentPostAttendance";
import { PaymentCRM } from "./components/PaymentCRM";
import { useOrganizationContext } from "@/contexts/OrganizationContext";
import { extensionService } from "@/services/subscription/ExtensionService";
import { currentFeatures } from "@/configs/Settings";

// Importa as extensões e interfaces dos arquivos separados
import { Extension, gestaoExtensions, iaExtensions } from "./data";
import { useProntoAtendimentoContractContext } from "@/contexts/ProntoAtendimentoContractContext";

/**
 * Componente FluxExtensions
 *
 * Página que exibe as extensões disponíveis e em desenvolvimento do Fluxmed.
 * Organiza as extensões em uma linha horizontal.
 */

// Componente principal com gerenciamento de visualização
export function FluxExtensions() {
  const location = useLocation();
  const { contractFluxPay } = useFluxPayContractContext();
  const { contractDiscussionCase } = useDiscussionCaseContext();
  const { contractMedicalReports } = useMedicalReportsAgentContext();
  const { contractGuidance } = useGuidanceAgentContext();
  const { contractPostAttendance } = usePostAttendanceContext();
  const { contractProntoAtendimento } = useProntoAtendimentoContractContext();
  const { contractHealthPlan } = useHealthPlanContractContext();
  const { contractCRM } = useCRMContractContext();
  const { contractFluxSigner } = useFluxSignerContext();
  const { contractQuestionnaires } = useQuestionnairesContractContext();
  const { currentContract } = useContractContext();
  const { organization } = useOrganizationContext();

  // Estado para controlar a visualização atual
  const [currentView, setCurrentView] = useState<
    | "main"
    | "post-attendance-activation"
    | "post-attendance-deactivation"
    | "crm-activation"
  >("main");

  // Destaque do card FluxZap ao vir de modais sem a extensão ativa
  const [highlightFluxZap, setHighlightFluxZap] = useState(false);

  useEffect(() => {
    const state = location.state as {
      activateCRM?: boolean;
      highlightFluxZap?: boolean;
    };
    if (state?.activateCRM) {
      setCurrentView("crm-activation");
      window.history.replaceState({}, document.title);
    }
    if (state?.highlightFluxZap) {
      setHighlightFluxZap(true);
      window.history.replaceState({}, document.title);
      requestAnimationFrame(() => {
        document
          .getElementById("extension-fluxzap")
          ?.scrollIntoView({ behavior: "smooth", block: "center" });
      });
    }
  }, [location.state]);

  // Estado para armazenar os planos de assinatura
  const [subscriptionPlans, setSubscriptionPlans] = useState<
    { plan: PreapprovalResponse }[]
  >([]);
  const [isLoadingPlans, setIsLoadingPlans] = useState(false);
  const [postAttendancePlan, setPostAttendancePlan] = useState<
    PreapprovalResponse | undefined
  >(undefined);

  // Função para buscar os planos de assinatura
  const fetchSubscriptionPlans = useCallback(async () => {
    if (currentView === "post-attendance-activation") {
      setIsLoadingPlans(true);
      try {
        const ExtensionService = new extensionService();

        const allPlans = await ExtensionService.getExtensionPlans();

        setSubscriptionPlans(allPlans);

        // Encontrar o plano de pós-atendimento
        const posAttendancePlan = allPlans.find((plan: any) => {
          const externalRef = String(
            plan.plan?.external_reference || ""
          ).toLowerCase();
          return externalRef === "pos-atendimento";
        });

        if (posAttendancePlan) {
          setPostAttendancePlan(posAttendancePlan.plan);
        } else {
          console.error("Nenhum plano de pós-atendimento disponível");
          toast.error(
            "Nenhum plano de pós-atendimento disponível. Entre em contato com o suporte."
          );
          setPostAttendancePlan(undefined);
        }
      } catch (error) {
        console.error("Erro ao buscar planos de assinatura:", error);
        toast.error("Erro ao buscar planos de assinatura");
      } finally {
        setIsLoadingPlans(false);
      }
    }
  }, [currentView, organization?.id]);

  // Efeito para buscar os planos quando a visualização mudar para ativação
  useEffect(() => {
    fetchSubscriptionPlans();
  }, [currentView]);

  // Verifica se o contrato atual tem uma assinatura
  const hasSubscription =
    currentContract?.[0]?.resource?.type?.coding?.some(
      (coding: any) =>
        coding.system ===
          "https://fluxmed.com.br/hapi/fhir/R5/contract-types" &&
        coding.code === "fluxPayPlan"
    ) ||
    currentContract?.[0]?.resource?.identifier?.some(
      (identifier: any) => identifier.value === "fluxPayPlan"
    );

  // Atualiza o status das extensões baseado nos contratos ativos
  const updatedGestaoExtensions = gestaoExtensions.map((ext) => {
    switch (ext.id) {
      case "marketplace":
        return { ...ext, status: contractFluxPay ? "Ativo" : "Disponível" };
      case "post_attendance":
        return {
          ...ext,
          status: contractPostAttendance ? "Ativo" : "Disponível",
        };
      case "health_plan":
        return { ...ext, status: "Em desenvolvimento" }; //contractHealthPlan ? "Ativo" : "Disponível" };
      case "pronto_atendimento":
        // Beta: badge fixo "Em desenvolvimento"; contrato ainda habilita cancelar / gate na agenda
        return { ...ext, status: contractProntoAtendimento ? "Ativo" : "Disponível" };
      case "crm":
        return { ...ext, status: contractCRM ? "Ativo" : "Disponível" };
      case "fluxsigner":
        return { ...ext, status: contractFluxSigner ? "Ativo" : "Disponível" };
      case "questionnaires":
        return { ...ext, status: contractQuestionnaires ? "Ativo" : "Disponível" };
      default:
        return ext;
    }
  });

  // Atualiza o status das extensões de IA baseado nos contratos ativos
  // Apenas processa quando defaultApp for true
  const updatedIaExtensions = currentFeatures.defaultApp
    ? iaExtensions.map((ext) => {
      switch (ext.id) {
        case "agent-case-discussion":
          return {
            ...ext,
            status: contractDiscussionCase ? "Ativo" : "Disponível",
          };
        case "agent-medical-report":
          return {
            ...ext,
            status: contractMedicalReports ? "Ativo" : "Disponível",
          };
        case "agent-guideline-assistant":
          return {
            ...ext,
            status: contractGuidance ? "Ativo" : "Disponível",
          };
        default:
          return ext;
      }
    })
    : [];

  // Filtra as extensões para remover o MarketplaceCard se houver assinatura
  // e remove a extensão TISS (health_plan) se defaultApp for false
  const filteredGestaoExtensions = updatedGestaoExtensions.filter((ext) => {
    // Remove marketplace se houver assinatura
    if (hasSubscription && ext.id === "marketplace") {
      return false;
    }
    // Remove health_plan (TISS) se defaultApp for false
    if (!currentFeatures.defaultApp && ext.id === "health_plan") {
      return false;
    }
    return true;
  });

  // Constantes para os cards de cada sessão usando map
  const gestaoCards = filteredGestaoExtensions.map((ext) => {
    switch (ext.id) {
      case "marketplace":
        return (
          <MarketplaceCard
            key={ext.id}
            title={ext.title}
            description={ext.description}
            status={ext.status}
            priceInfo={ext.priceInfo}
          />
        );
      case "post_attendance":
        return (
          <PostAttendanceCard
            key={ext.id}
            id="extension-fluxzap"
            highlighted={highlightFluxZap}
            title={ext.title}
            description={ext.description}
            status={ext.status}
            onActivate={() => setCurrentView("post-attendance-activation")}
            onDeactivate={() => setCurrentView("post-attendance-deactivation")}
            priceInfo={ext.priceInfo}
          />
        );
      case "google_calendar":
        return <GoogleAuthCard key={ext.id} priceInfo={ext.priceInfo} />;
      case "health_plan":
        return (
          <HealthPlanCard
            key={ext.id}
            title={ext.title}
            description={ext.description}
            status={ext.status}
            priceInfo={ext.priceInfo}
          />
        );
      case "pronto_atendimento":
        return (
          <ProntoAtendimentoExtensionCard
            key={ext.id}
            title={ext.title}
            description={ext.description}
            status={ext.status}
            priceInfo={ext.priceInfo}
          />
        );
      case "crm":
        return (
          <CRMCard
            key={ext.id}
            title={ext.title}
            description={ext.description}
            status={ext.status}
            priceInfo={ext.priceInfo}
            onActivate={() => setCurrentView("crm-activation")}
          />
        );
      case "fluxsigner":
        return (
          <FluxSignerCard
            key={ext.id}
            title={ext.title}
            description={ext.description}
            status={ext.status}
            priceInfo={ext.priceInfo}
          />
        );
      case "questionnaires":
        return (
          <QuestionnairesCard
            key={ext.id}
            title={ext.title}
            description={ext.description}
            status={ext.status}
            priceInfo={ext.priceInfo}
          />
        );
      default:
        return (
          <ExtensionCard
            key={ext.id}
            title={ext.title}
            description={ext.description}
            buttonText={
              ext.disabled ? "Aguardando lançamento" : "Obter Extensão"
            }
            status={ext.status}
            buttonDisabled={ext.disabled}
            onButtonClick={
              ext.disabled ? undefined : () => console.log("extensão")
            }
          />
        );
    }
  });

  // Cria os cards de IA apenas quando defaultApp for true
  const iaCards = currentFeatures.defaultApp
    ? updatedIaExtensions.map((ext: Extension) => {
      switch (ext.id) {
        case "agent-case-discussion":
          return (
            <DiscussionCaseCard
              key={ext.id}
              title={ext.title}
              description={ext.description}
              status={contractDiscussionCase ? "Ativo" : ext.status}
              priceInfo={ext.priceInfo}
            />
          );
        case "agent-medical-report":
          return (
            <MedicalReportsAgentCard
              key={ext.id}
              title={ext.title}
              description={ext.description}
              status={contractMedicalReports ? "Ativo" : ext.status}
              priceInfo={ext.priceInfo}
            />
          );
        case "agent-guideline-assistant":
          return (
            <GuidanceAgentCard
              key={ext.id}
              title={ext.title}
              description={ext.description}
              status={contractGuidance ? "Ativo" : ext.status}
              priceInfo={ext.priceInfo}
            />
          );
        default:
          return (
            <ExtensionCard
              key={ext.id}
              title={ext.title}
              description={ext.description}
              buttonText={
                ext.disabled ? "Aguardando lançamento" : "Obter Extensão"
              }
              status={ext.status}
              buttonDisabled={ext.disabled}
              onButtonClick={
                ext.disabled ? undefined : () => console.log("extensão", ext)
              }
            />
          );
      }
    })
    : [];

  return (
    <Box sx={{ minHeight: "100vh", width: "100%", overflow: "visible" }}>
      <LargeContentBox>
        {currentView === "main" && (
          <Stack width="100%" spacing={1.5}>
            <MainPageTitle subtitle="Descubra e potencialize sua clínica com as extensões Fluxmed.">
              Extensões do Fluxmed
            </MainPageTitle>

            <Stack width="100%" spacing={5}>
              {/* Sessão Gestão */}
              <Stack spacing={2}>
                <SectionTitle
                  text="Gestão"
                  subtitle="Extensões para facilitar a gestão financeira e de agenda da sua clínica."
                />
                <Grid container spacing={4}>
                  {gestaoCards.map((card, index) => (
                    <Grid
                      item
                      xs={12}
                      sx={{
                        "@media (min-width: 900px)": {
                          width: "calc(50% - 32px)",
                          flexBasis: "calc(50% - 32px)",
                        },
                        "@media (min-width: 1400px)": {
                          width: "calc(33.333% - 32px)",
                          flexBasis: "calc(33.333% - 32px)",
                        },
                      }}
                      key={index}
                    >
                      {card}
                    </Grid>
                  ))}
                </Grid>
              </Stack>

              {/* Sessão Integração com IA - Exibida apenas quando defaultApp for true */}
              {currentFeatures.defaultApp && (
                <Stack spacing={2}>
                  <SectionTitle
                    text={
                      <>
                        Integração com IA{" "}
                        <Box
                          component="span"
                          sx={{
                            color: "warning.main",
                            fontSize: "0.8em",
                            ml: 1,
                          }}
                        >
                          (BETA)
                        </Box>
                      </>
                    }
                    subtitle="Potencialize sua prática clínica com recursos avançados de Inteligência Artificial, como sugestões de diagnóstico, transcrição automática e análise de tendências de saúde."
                  />
                  <Grid container spacing={4}>
                    {iaCards.map((card, index) => (
                      <Grid
                        item
                        xs={12}
                        sx={{
                          "@media (min-width: 900px)": {
                            width: "calc(50% - 32px)",
                            flexBasis: "calc(50% - 32px)",
                          },
                          "@media (min-width: 1400px)": {
                            width: "calc(33.333% - 32px)",
                            flexBasis: "calc(33.333% - 32px)",
                          },
                        }}
                        key={index}
                      >
                        {card}
                      </Grid>
                    ))}
                  </Grid>
                </Stack>
              )}
            </Stack>
          </Stack>
        )}

        {currentView === "post-attendance-activation" && (
          <PaymentPostAttendance
            mode="activation"
            onGoBack={() => setCurrentView("main")}
            postAttendancePlan={postAttendancePlan as PreapprovalResponse}
            isLoading={isLoadingPlans}
          />
        )}
        {currentView === "post-attendance-deactivation" && (
          <PaymentPostAttendance
            mode="deactivation"
            onGoBack={() => setCurrentView("main")}
          />
        )}
        {currentView === "crm-activation" && (
          <PaymentCRM onGoBack={() => setCurrentView("main")} />
        )}
      </LargeContentBox>
    </Box>
  );
}
